import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { CheckCircle2, ChevronRight, MessageSquare } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { StatusPill } from '@/components/status-pill'
import { OrderStatusChips } from '@/components/order-status-chips'
import { Badge } from '@/components/ui/badge'
import { StaffAssignmentPicker } from '@/components/staff-assignment-picker'
import { useUpdateProject } from '@/api/hooks'
import { cn, formatEventTime, formatStatus, parseUTCDate } from '@/lib/utils'
import { ActionPriorityPicker, ProjectPriorityPicker } from '@/components/priority-picker'
import { InProgressTag } from '@/features/purchase-orders/action-card-parts'
import { useActorName } from '@/hooks/use-actor-name'
import { compactAge, isPastDue, shortDate } from '@/features/purchase-orders/action-card-model'
import { PIPELINE_CATEGORY_COLORS } from '@/lib/status'
import { actionTarget } from '@/features/action-items/action-target'
import type { components } from '@/api/schema'

type Project = components['schemas']['InternalProjectWithActionsRead']
type ProjectAction = components['schemas']['ProjectOpenActionRead']

/** Accounting's invoice items whose invoice has no project, listed as one group first (USA-1184). */
export type WithoutProjectGroup = {
  actions: ProjectAction[]
  total: number
  expanded: boolean
  onToggle: () => void
}

type RoleField = 'sales_person_id' | 'ops_person_id' | 'warehousing_person_id' | 'accounting_person_id'
type RoleObjField = 'sales_person' | 'ops_person' | 'warehousing_person' | 'accounting_person'

const ROLES: { label: string; idField: RoleField; objField: RoleObjField; actionRoles: string[] }[] = [
  { label: 'Sales', idField: 'sales_person_id', objField: 'sales_person', actionRoles: ['sales'] },
  { label: 'Ops', idField: 'ops_person_id', objField: 'ops_person', actionRoles: ['ops', 'operations'] },
  {
    label: 'Warehouse',
    idField: 'warehousing_person_id',
    objField: 'warehousing_person',
    actionRoles: ['warehousing'],
  },
  {
    label: 'Accounting',
    idField: 'accounting_person_id',
    objField: 'accounting_person',
    actionRoles: ['accounting'],
  },
]

function actionRoles(action: ProjectAction) {
  return [action.assigned_role, ...(action.shared_roles ?? [])].filter((role): role is string => !!role)
}

function POSummaryBadge({ summary }: { summary: string | null | undefined }) {
  if (!summary) return <span className="text-xs text-muted-foreground">No POs</span>
  const cat = PIPELINE_CATEGORY_COLORS[summary]
  if (!cat)
    return (
      <Badge variant="secondary" className="text-xs">
        {formatStatus(summary)}
      </Badge>
    )
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cat.bg} ${cat.text}`}
    >
      {cat.label}
    </span>
  )
}

/** The single link target for an action item (see actionTarget), scrolled to the item. */
export function ActionLink({
  action,
  projectUuid,
  className,
  children,
}: {
  action: ProjectAction
  projectUuid: string
  className: string
  children: ReactNode
}) {
  // An order review finding (USA-1513) opens its own review page.
  if (action.action_source === 'manufacturer_data' && action.card?.order_finding_uuid) {
    return (
      <Link
        to="/order-review/$findingId"
        params={{ findingId: action.card.order_finding_uuid }}
        className={className}
      >
        {children}
      </Link>
    )
  }
  // A manufacturer data item (USA-1474) is worked on its manufacturer's Emails tab.
  if (action.action_source === 'manufacturer_data' && action.manufacturer_uuid) {
    const email = action.card?.source_email
    return (
      <Link
        to="/manufacturers/$manuId"
        params={{ manuId: action.manufacturer_uuid }}
        search={email ? { tab: 'emails', message: String(email.message_id) } : { tab: 'emails', action: action.uuid }}
        className={className}
      >
        {children}
      </Link>
    )
  }
  const target = actionTarget(action, projectUuid)
  const messageId = 'messageId' in target ? target.messageId : undefined
  const search = messageId != null ? { message: String(messageId) } : { action: action.uuid }
  switch (target.kind) {
    case 'customer_thread':
      return (
        <Link
          to="/customer-comms/$threadId"
          params={{ threadId: String(target.threadId) }}
          search={search}
          className={className}
        >
          {children}
        </Link>
      )
    case 'purchase_order':
      return (
        <Link to="/purchase-orders/$poId" params={{ poId: target.poId }} search={search} className={className}>
          {children}
        </Link>
      )
    case 'order':
      return (
        <Link to="/orders/$orderId" params={{ orderId: target.orderId }} search={search} className={className}>
          {children}
        </Link>
      )
    case 'project':
      return (
        <Link to="/projects/$projectId" params={{ projectId: target.projectId }} search={search} className={className}>
          {children}
        </Link>
      )
    case 'tax_exemption':
      return (
        <Link to="/tax-exemptions/$formId" params={{ formId: target.formId }} className={className}>
          {children}
        </Link>
      )
    // Warranty@ mail no claim holds yet (USA-915) opens on the Warranty claims page.
    case 'warranty_item':
      return (
        <Link to="/warranty-claims" search={{ action: target.actionUuid }} className={className}>
          {children}
        </Link>
      )
    case 'invoice':
      return (
        <Link
          to="/invoices/$invoiceId"
          params={{ invoiceId: target.invoiceId }}
          search={{ action: target.actionUuid }}
          className={className}
        >
          {children}
        </Link>
      )
  }
}

/** An action at a glance: priority, due and age on the left; its short kind, PO and headline beside them. */
export function ActionRowBody({
  action,
  projectUuid,
}: {
  action: ProjectAction
  /** Makes the row a link to the item (stretched over the whole row) with a priority menu staff can change. */
  projectUuid?: string
}) {
  const actorName = useActorName()
  const working = action.in_progress
  // A closed item, listed by the Resolved filter (USA-1653): who closed it and when, instead of its priority.
  const resolvedAt = action.status === 'resolved' || action.status === 'dismissed' ? action.resolved_at : null
  const isOrderLineAction = action.action_source === 'order_line'
  const isCustomerEmail = action.action_source === 'customer_communication'
  const isPoReview = action.action_type === 'po_review'
  const reference = isPoReview
    ? null
    : isOrderLineAction
      ? action.po_number || action.order_number || 'Sales order'
      : isCustomerEmail
        ? action.order_number
      : action.po_number
  const invoice = action.card?.invoice
  const pastDue = action.expected_date ? isPastDue(action.expected_date) : false
  const created = parseUTCDate(action.created_at)
  const source = [
    action.manufacturer_name && !isPoReview ? action.manufacturer_name : null,
    isCustomerEmail
      ? `${action.tax_exemption_uuid ? 'Store customer' : 'Customer email'}${action.customer_name ? ` · ${action.customer_name}` : ''}`
      : null,
  ].filter(Boolean)

  const main = (
    <span className="min-w-0">
      <span className="block text-sm font-medium leading-tight text-foreground">
        {action.card?.kind_label ?? formatStatus(action.action_type)}
        {action.card?.sublabel && (
          <span className="font-normal text-muted-foreground"> · {action.card.sublabel}</span>
        )}
        {action.comment_count > 0 && (
          <span className="ml-2 inline-flex items-center gap-0.5 align-middle text-xs font-normal text-muted-foreground">
            <MessageSquare className="size-3" aria-hidden />
            {action.comment_count}
            <span className="sr-only"> comment{action.comment_count === 1 ? '' : 's'}</span>
          </span>
        )}
      </span>
      {(reference || invoice || source.length > 0) && (
        <span className="mt-0.5 block truncate text-xs leading-tight text-muted-foreground">
          {reference && <span className="font-mono">{reference}</span>}
          {/* The supplier invoice an accounting item is about opens directly, above the row's link (USA-103). */}
          {invoice && (
            <>
              {reference && ' · '}
              {projectUuid !== undefined ? (
                <Link
                  to="/invoices/$invoiceId"
                  params={{ invoiceId: invoice.uuid }}
                  className="relative z-10 text-foreground underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-foreground"
                >
                  {invoice.label}
                </Link>
              ) : (
                invoice.label
              )}
            </>
          )}
          {(reference || invoice) && source.length > 0 && ' · '}
          {source.join(' · ')}
        </span>
      )}
      <span
        className="mt-1 line-clamp-2 text-[13px] leading-snug text-foreground/80"
        title={action.card?.headline ?? action.summary}
      >
        {action.card?.headline ?? action.summary}
      </span>
      {resolvedAt && (
        <span className="mt-1 block text-xs leading-tight text-muted-foreground">
          {formatStatus(action.status)}
          {action.resolved_by && ` by ${actorName(action.resolved_by)}`} ·{' '}
          <time dateTime={resolvedAt}>{formatEventTime(resolvedAt)}</time>
        </span>
      )}
      {working && (
        <span className="mt-1 block text-xs leading-tight text-sky-800 dark:text-sky-300">
          Started by {actorName(working.started_by) ?? 'staff'} ·{' '}
          <time dateTime={working.started_at} title={parseUTCDate(working.started_at).toLocaleString()}>
            {compactAge(working.started_at)}
          </time>
        </span>
      )}
    </span>
  )

  return (
    <>
      <span className="flex flex-col items-start gap-1 pt-px text-xs text-muted-foreground">
        {resolvedAt ? (
          <StatusPill tone={action.status === 'dismissed' ? 'neutral' : 'success'} className="whitespace-nowrap">
            {formatStatus(action.status)}
          </StatusPill>
        ) : (
          // Above the row's stretched link, so it opens the picker instead of the item.
          <span className={projectUuid !== undefined ? 'relative z-10' : undefined}>
            <ActionPriorityPicker source={action.action_source} actionUuid={action.uuid} priority={action.priority} />
            <span className="sr-only"> priority</span>
          </span>
        )}
        {working && <InProgressTag />}
        {action.expected_date && !resolvedAt && (
          <span className={cn('tabular-nums', pastDue ? 'font-medium text-red-700 dark:text-red-400' : 'font-medium text-amber-700 dark:text-amber-400')}>
            {pastDue ? 'Overdue ' : 'Due '}
            {shortDate(action.expected_date)}
          </span>
        )}
        <time dateTime={action.created_at} className="tabular-nums" title={`Raised ${created.toLocaleString()}`}>
          {compactAge(action.created_at)}
        </time>
      </span>
      {projectUuid !== undefined ? (
        <ActionLink
          action={action}
          projectUuid={projectUuid}
          className="block min-w-0 outline-none after:absolute after:inset-0 after:content-['']"
        >
          {main}
        </ActionLink>
      ) : (
        main
      )}
    </>
  )
}

/** A row whose item link stretches over it (see ActionRowBody); the whole row lights up on hover and focus. */
export const ACTION_ROW_CLASS =
  'group relative grid grid-cols-[5.5rem_minmax(0,1fr)_auto] items-start gap-x-3 px-3 py-2.5 transition-colors hover:bg-muted/50 has-[a:focus-visible]:bg-muted/50 has-[a:focus-visible]:ring-2 has-[a:focus-visible]:ring-inset has-[a:focus-visible]:ring-ring/50'

export function ActionRowChevron() {
  return (
    <ChevronRight
      className="mt-0.5 size-4 shrink-0 text-muted-foreground/60 transition-transform group-hover:translate-x-0.5 group-hover:text-foreground"
      aria-hidden="true"
    />
  )
}

function ActionRow({ action, projectUuid }: { action: ProjectAction; projectUuid: string }) {
  return (
    <li className={ACTION_ROW_CLASS}>
      <ActionRowBody action={action} projectUuid={projectUuid} />
      <ActionRowChevron />
    </li>
  )
}

function ActionRows({ actions, label, projectUuid }: { actions: ProjectAction[]; label: string; projectUuid: string }) {
  return (
    <ul aria-label={label} className="divide-y overflow-hidden rounded-lg border bg-background">
      {actions.map((action) => (
        <ActionRow key={`${action.action_source}-${action.uuid}`} action={action} projectUuid={projectUuid} />
      ))}
    </ul>
  )
}

function WithoutProjectActions({ group }: { group: WithoutProjectGroup }) {
  const more = group.total - group.actions.length
  return (
    <div className="space-y-1.5">
      <ActionRows actions={group.actions} label="Action items with no project" projectUuid="" />
      {(more > 0 || group.expanded) && (
        <button
          type="button"
          onClick={group.onToggle}
          className="cursor-pointer rounded-sm px-1 text-xs font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          {group.expanded ? 'Show fewer' : `Show all ${group.total}`}
        </button>
      )}
    </div>
  )
}

function WithoutProjectDetails() {
  return <p className="font-medium">No project</p>
}

function ActionList({ project }: { project: Project }) {
  const actions = project.actions ?? []
  if (actions.length === 0) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <CheckCircle2 className="h-4 w-4 text-green-500" />
        No open actions
      </div>
    )
  }
  return <ActionRows actions={actions} label={`Action items for ${project.name}`} projectUuid={project.uuid} />
}

function TeamMember({
  project,
  role,
  attention,
}: {
  project: Project
  role: (typeof ROLES)[number]
  attention: boolean
}) {
  const updateProject = useUpdateProject(project.uuid)
  return (
    <StaffAssignmentPicker
      label={role.label}
      currentUser={project[role.objField]}
      pending={updateProject.isPending}
      showName
      attention={attention}
      onChange={(user) => updateProject.mutate({ [role.idField]: user?.id ?? null })}
      className="min-h-8 text-xs"
    />
  )
}

function TeamAssignments({ project }: { project: Project }) {
  const rolesWithActions = new Set((project.actions ?? []).flatMap(actionRoles))
  return (
    <div className="flex flex-wrap gap-1.5">
      {ROLES.map((role) => (
        <TeamMember
          key={role.idField}
          project={project}
          role={role}
          attention={role.actionRoles.some((actionRole) => rolesWithActions.has(actionRole))}
        />
      ))}
    </div>
  )
}

function ProjectDetails({ project }: { project: Project }) {
  const summary = project.status_summary
  return (
    <div className="space-y-2">
      <div className="space-y-0.5">
        <div className="flex items-start justify-between gap-2">
          <Link
            to="/projects/$projectId"
            params={{ projectId: project.uuid }}
            className="font-medium text-primary hover:underline"
          >
            {project.name}
          </Link>
          <ProjectPriorityPicker projectUuid={project.uuid} priority={project.priority} align="end" />
        </div>
        <p className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          {project.customer_group && (
            <Link
              to="/customers/$groupId"
              params={{ groupId: project.customer_group.uuid }}
              className="hover:text-primary hover:underline"
            >
              {project.customer_group.name}
            </Link>
          )}
          {project.date_placed && (
            <span className="tabular-nums">
              Placed {parseUTCDate(project.date_placed).toLocaleDateString()}
            </span>
          )}
        </p>
      </div>
      {summary ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {!summary.order && !summary.shipping && (
            <StatusPill tone={summary.headline.tone}>{summary.headline.label}</StatusPill>
          )}
          <OrderStatusChips order={summary.order} shipping={summary.shipping} ap={summary.ap} ar={summary.ar} />
        </div>
      ) : (
        <POSummaryBadge summary={project.po_status_summary} />
      )}
    </div>
  )
}

function ProjectTableRow({ project }: { project: Project }) {
  return (
    <TableRow className="align-top hover:bg-transparent">
      <TableCell className="align-top whitespace-normal">
        <div className="space-y-3">
          <ProjectDetails project={project} />
          <TeamAssignments project={project} />
        </div>
      </TableCell>
      <TableCell className="align-top whitespace-normal">
        <ActionList project={project} />
      </TableCell>
    </TableRow>
  )
}

function ProjectCard({ project }: { project: Project }) {
  return (
    <div className="space-y-3 rounded-lg border bg-card p-3">
      <ProjectDetails project={project} />
      <TeamAssignments project={project} />
      <ActionList project={project} />
    </div>
  )
}

export function ActionItemsTableSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <>
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[38%]">Project</TableHead>
              <TableHead>Action Items</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: rows }).map((_, i) => (
              <TableRow key={i}>
                <TableCell>
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-4 w-40" />
                    <div className="grid grid-cols-2 gap-1.5">
                      {Array.from({ length: 4 }).map((_, j) => (
                        <Skeleton key={j} className="h-8 rounded-full" />
                      ))}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Skeleton className="h-24 w-full rounded-lg" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="space-y-2 lg:hidden">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="space-y-2 rounded-lg border bg-card p-3">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
            <Skeleton className="h-16 w-full" />
          </div>
        ))}
      </div>
    </>
  )
}

export function ActionItemsTable({
  items,
  withoutProject,
  withoutProjectAt = 0,
}: {
  items: Project[]
  /** Shown first when it has items. */
  withoutProject?: WithoutProjectGroup
  /** How many projects come before the "No project" group (see withoutProjectPlace). */
  withoutProjectAt?: number
}) {
  const showWithoutProject = !!withoutProject && withoutProject.actions.length > 0
  const before = items.slice(0, withoutProjectAt)
  const after = items.slice(withoutProjectAt)
  return (
    <>
      {/* Desktop table */}
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[38%]">Project</TableHead>
              <TableHead>Action Items</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {before.map((project) => (
              <ProjectTableRow key={project.id} project={project} />
            ))}
            {showWithoutProject && (
              <TableRow className="align-top hover:bg-transparent">
                <TableCell className="align-top whitespace-normal">
                  <WithoutProjectDetails />
                </TableCell>
                <TableCell className="align-top whitespace-normal">
                  <WithoutProjectActions group={withoutProject} />
                </TableCell>
              </TableRow>
            )}
            {after.map((project) => (
              <ProjectTableRow key={project.id} project={project} />
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile stacked cards */}
      <div className="space-y-3 lg:hidden">
        {before.map((project) => (
          <ProjectCard key={project.id} project={project} />
        ))}
        {showWithoutProject && (
          <div className="space-y-3 rounded-lg border bg-card p-3">
            <WithoutProjectDetails />
            <WithoutProjectActions group={withoutProject} />
          </div>
        )}
        {after.map((project) => (
          <ProjectCard key={project.id} project={project} />
        ))}
      </div>
    </>
  )
}
